import { supabase } from './supabase'
import type { SupportLane } from './support'

/**
 * The staff side of a private question — 117, HLP-27.
 *
 * Three things only the lane holder sees, and the asker never does:
 *
 *   triage   the daily Claude run's reading of the ticket — MACHINE-written,
 *            advisory, replaced on re-triage. The panel paints it teal.
 *   notes    colleague notes. NOT comments: every comment on a private question
 *            is read by the asker (089), and a note must not be.
 *   handoff  the de-identified rewrite a holder approved for developers, and
 *            what the tracker said about it the last time the run looked.
 *
 * `support_staff_view()` REFUSES for anybody not holding the question's lane
 * (rule 12), so a caller who is not staff gets an error, never an empty panel
 * that would read as "nobody has triaged this".
 */

export type TriageCategory = 'bug' | 'idea' | 'how_to' | 'not_platform' | 'other'
export type TriageSeverity = 'low' | 'normal' | 'high'
/** The tracker's statuses. `ready` is what "approved for development" means. */
export type TrackerStatus = 'backlog' | 'ready' | 'doing' | 'done'

export type SupportTriage = {
  category: TriageCategory
  severity: TriageSeverity
  summary: string
  suspectedCause: string | null
  recommendation: string | null
  devTitleDraft: string | null
  devBodyDraft: string | null
  model: string
  triagedAt: string
}

export type SupportHandoff = {
  title: string
  body: string
  requestedBy: string | null
  requestedAt: string
  trackerIssueId: string | null
  filedAt: string | null
  /** A COPY of the tracker's status, never authoritative. Always read with
   *  `trackerStatusSeenAt` — without the time it would be a claim. */
  trackerStatus: TrackerStatus | null
  trackerStatusSeenAt: string | null
}

export type SupportNote = {
  id: string
  /** 123. The reader's-language rendering when One made one; else the words. */
  body: string
  author: string | null
  createdAt: string
  /** 123. 'machine' when `body` is One's; the author's words ride beside it
   *  because a note is short and the panel has no second call to make. */
  translationStatus: 'machine' | null
  originalBody: string | null
  originalLocale: string
}

/* ── 120: A PRIVATE THREAD BECOMES A WIKI ONE DRAFT ──────────────────────────
 *
 * Three hands. The MACHINE drafts a general question and answer from the
 * thread (no names) into `support_wiki_draft` — advisory, teal on screen. The
 * lane HOLDER edits and confirms: `draft_support_answer()` makes a Wiki One
 * answer DRAFT authored by them and links it from the question. A CURATOR
 * publishes it from curator mode, because publishing needs `content.publish`,
 * which 085 withheld from the support roles on purpose. The asker is told
 * when it goes PUBLIC, not when it is drafted (HLP-7).
 */
export type WikiDraft = {
  usable: boolean
  reason: string | null
  question: string | null
  answer: string | null
  model: string
  draftedAt: string
}

/** The Wiki One answer this thread became, and where it stands. `state` is
 *  the content row's, read live — not a cached copy. */
export type WikiAnswerLink = {
  contentId: string
  state: 'draft' | 'published' | string
  publishedAt: string | null
  title: string | null
}

export type SupportStaffView = {
  lane: SupportLane
  triage: SupportTriage | null
  handoff: SupportHandoff | null
  notes: SupportNote[]
  /** 120 */
  wikiDraft: WikiDraft | null
  wikiAnswer: WikiAnswerLink | null
}

export async function loadSupportStaffView(questionId: string): Promise<SupportStaffView> {
  const { data, error } = await supabase.rpc('support_staff_view', { p_content_id: questionId })
  if (error) throw new Error(error.message)
  const r = ((data ?? []) as Record<string, unknown>[])[0]
  if (!r) throw new Error('no such private question in a lane you answer')
  return staffViewFromRow(r)
}

/** Exported so the suite can feed it a row without a network. */
export function staffViewFromRow(r: Record<string, unknown>): SupportStaffView {
  return {
    lane: r.lane as SupportLane,
    triage: r.category
      ? {
          category: r.category as TriageCategory,
          severity: r.severity as TriageSeverity,
          summary: r.summary as string,
          suspectedCause: (r.suspected_cause as string) ?? null,
          recommendation: (r.recommendation as string) ?? null,
          devTitleDraft: (r.dev_title_draft as string) ?? null,
          devBodyDraft: (r.dev_body_draft as string) ?? null,
          model: r.model as string,
          triagedAt: r.triaged_at as string,
        }
      : null,
    handoff: r.handoff_title
      ? {
          title: r.handoff_title as string,
          body: r.handoff_body as string,
          requestedBy: (r.requested_by as string) ?? null,
          requestedAt: r.requested_at as string,
          trackerIssueId: (r.tracker_issue_id as string) ?? null,
          filedAt: (r.filed_at as string) ?? null,
          trackerStatus: (r.tracker_status as TrackerStatus) ?? null,
          trackerStatusSeenAt: (r.tracker_status_seen_at as string) ?? null,
        }
      : null,
    notes: ((r.notes as Record<string, unknown>[] | null) ?? []).map((n) => ({
      id: n.id as string,
      body: n.body as string,
      author: (n.author as string) ?? null,
      createdAt: n.created_at as string,
      translationStatus: n.translation_status === 'machine' ? 'machine' : null,
      originalBody: (n.original_body as string) ?? null,
      originalLocale: (n.original_locale as string) ?? 'en-CA',
    })),
    wikiDraft: r.wiki_draft
      ? {
          usable: (r.wiki_draft as Record<string, unknown>).usable === true,
          reason: ((r.wiki_draft as Record<string, unknown>).reason as string) ?? null,
          question: ((r.wiki_draft as Record<string, unknown>).question as string) ?? null,
          answer: ((r.wiki_draft as Record<string, unknown>).answer as string) ?? null,
          model: (r.wiki_draft as Record<string, unknown>).model as string,
          draftedAt: (r.wiki_draft as Record<string, unknown>).drafted_at as string,
        }
      : null,
    wikiAnswer: r.wiki_answer
      ? {
          contentId: (r.wiki_answer as Record<string, unknown>).content_id as string,
          state: (r.wiki_answer as Record<string, unknown>).state as string,
          publishedAt: ((r.wiki_answer as Record<string, unknown>).published_at as string) ?? null,
          title: ((r.wiki_answer as Record<string, unknown>).title as string) ?? null,
        }
      : null,
  }
}

/**
 * 120. Ask the machine for a first draft of the Wiki One entry this thread
 * could become. `publish-post` checks the caller can staff the question, reads
 * the thread as sides with no names, and stores the draft; nothing here
 * enters content. Re-asking replaces the draft.
 */
export async function requestWikiDraft(questionId: string): Promise<{ usable: boolean }> {
  const { data, error } = await supabase.functions.invoke('publish-post', {
    body: { kind: 'support_wiki_draft', content_id: questionId },
  })
  if (error) throw new Error(error.message)
  const d = data as { drafted?: boolean; usable?: boolean; error?: string } | null
  if (!d?.drafted) throw new Error(d?.error ?? 'the model did not return a draft')
  return { usable: d.usable === true }
}

/**
 * 120. The holder confirms: a Wiki One answer DRAFT in their name. Returns the
 * new answer's id. The server refuses everybody but a holder of the lane, and
 * refuses a second draft for one thread.
 */
export async function draftSupportAnswer(questionId: string, question: string, answer: string): Promise<string> {
  const { data, error } = await supabase.rpc('draft_support_answer', {
    p_content_id: questionId,
    p_question: question,
    p_answer: answer,
  })
  if (error) throw new Error(error.message)
  return data as string
}

/** Where the Wiki One answer stands, in words — one home for the sentence. */
export function wikiAnswerState(a: WikiAnswerLink | null): string {
  if (!a) return 'Not on Wiki One.'
  if (a.state === 'published') return 'Published on Wiki One — One answers from it now.'
  return 'Drafted to Wiki One — waiting for a curator to publish it.'
}

export async function addSupportNote(questionId: string, body: string): Promise<void> {
  const { error } = await supabase.rpc('add_support_note', { p_content_id: questionId, p_body: body })
  if (error) throw new Error(error.message)
}

/**
 * Approve a rewrite for the developers. A REPLACE (rule 9): both texts every
 * time. The server refuses any lane but platform, and refuses once the tracker
 * has it — from then on the tracker issue is where it changes.
 */
export async function sendToDevelopers(questionId: string, title: string, body: string): Promise<void> {
  const { error } = await supabase.rpc('send_to_developers', {
    p_content_id: questionId,
    p_title: title,
    p_body: body,
  })
  if (error) throw new Error(error.message)
}

/** Where a handoff stands, in words — one home for the sentence (rule 11). */
export function handoffState(h: SupportHandoff | null): string {
  if (!h) return 'Not sent to developers.'
  if (!h.trackerIssueId) return 'Approved for developers — waiting for the daily run to file it.'
  const seen = h.trackerStatusSeenAt ? ` (as of ${new Date(h.trackerStatusSeenAt).toLocaleDateString('en-CA')})` : ''
  switch (h.trackerStatus) {
    case 'ready':
      return `${h.trackerIssueId} is approved for development${seen}.`
    case 'doing':
      return `${h.trackerIssueId} is being built${seen}.`
    case 'done':
      return `${h.trackerIssueId} is done${seen} — tell the asker in the thread.`
    default:
      return `${h.trackerIssueId} is waiting for a developer to approve it${seen}.`
  }
}

export const CATEGORY_WORDS: Record<TriageCategory, string> = {
  bug: 'Bug',
  idea: 'Idea',
  how_to: 'How-to question',
  not_platform: 'Not a platform problem',
  other: 'Other',
}
